import { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import brandImage from '@/assets/images/auth-brand.jpg'
import logo from '@/assets/logo-short.svg'
import logoLight from '@/assets/logo-short-white.svg'
import { AuthLanguageSwitcher } from './AuthLanguageSwitcher'

interface AuthLayoutProps {
  title: ReactNode
  subtitle?: ReactNode
  children: ReactNode
  /** Rendered below the form (e.g. "Don't have an account? Sign up"). */
  footer?: ReactNode
}

export function AuthLayout({
  title,
  subtitle,
  children,
  footer
}: AuthLayoutProps) {
  const { t } = useTranslation('auth')

  return (
    <div className="auth-scope flex min-h-screen w-screen overflow-x-hidden bg-background">
      {/* Brand panel */}
      <aside className="relative hidden w-1/2 overflow-hidden lg:flex">
        <img
          src={brandImage}
          alt=""
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/85 via-primary/55 to-ink/70" />
        <div className="relative z-10 flex h-full w-full flex-col justify-between p-12">
          <img src={logoLight} alt="SNA" className="h-10 w-auto self-start" />
          <div className="max-w-md">
            <h2 className="font-display text-4xl font-bold leading-tight text-white">
              {t('brand.title')}
            </h2>
            <p className="mt-4 text-lg text-white/85">{t('brand.subtitle')}</p>
          </div>
        </div>
      </aside>

      {/* Form panel */}
      <main className="relative flex w-full flex-col items-center justify-center px-6 py-12 lg:w-1/2">
        <div className="absolute right-6 top-6">
          <AuthLanguageSwitcher />
        </div>

        <div className="w-full max-w-md">
          <img
            src={logo}
            alt="SNA"
            className="mb-10 h-10 w-auto lg:hidden"
          />

          {title && (
            <div className="mb-8">
              <h1 className="font-display text-3xl font-bold text-ink">
                {title}
              </h1>
              {subtitle && (
                <p className="mt-2 text-base text-muted-foreground">
                  {subtitle}
                </p>
              )}
            </div>
          )}

          {children}

          {footer && (
            <div className="mt-8 text-center text-sm text-muted-foreground">
              {footer}
            </div>
          )}
        </div>
      </main>
    </div>
  )
}
